import React, { Fragment } from 'react';
import cx from 'classnames';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  size as sizing,
  useFloating,
} from '@floating-ui/react';
import { Listbox, Transition } from '@headlessui/react';

import Portal from '../HOC/Portal';
import { ArrowDown, Check } from '../Icons';

/**
 * Reference to https://codesandbox.io/s/gallant-sea-rcg43b?file=/src/App.tsx:2592-2616
 */

export interface DropdownSelectProps {
  options: Array<React.ReactElement>;
  text: string;
  size?: 'sm' | 'md';
  className?: string;
  value?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
}

const DropdownSelect = ({
  options,
  text,
  size = 'md',
  className,
  value,
  onChange = () => {},
  disabled,
}: DropdownSelectProps) => {
  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(),
      flip({ padding: 10 }),
      sizing({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 5,
      }),
    ],
  });

  return (
    <Listbox
      as="div"
      className={cx('dropdown-container', size, className && className)}
      disabled={disabled}
      value={value}
      onChange={onChange}
    >
      {({ open }) => (
        <>
          <Listbox.Button className="dropdown-button" ref={refs.setReference}>
            {({ value }) => (
              <>
                <div
                  className={cx('flex justify-start font-bold', {
                    'text-xs my-[8.5px]': size === 'sm',
                    'text-sm my-[9px]': size === 'md',
                    'text-black text-opacity-50': disabled,
                  })}
                >
                  {value ?? text}
                </div>
                <ArrowDown
                  width={size === 'sm' ? 18 : 24}
                  height={size === 'sm' ? 18 : 24}
                  className={cx(
                    'absolute',
                    disabled ? 'fill-black opacity-50' : 'fill-uta-black',
                    {
                      'right-2.5': size === 'md',
                      'right-2': size === 'sm',
                    }
                  )}
                />
              </>
            )}
          </Listbox.Button>

          <Portal>
            <FloatingFocusManager context={context} modal={false}>
              <Transition
                show={open}
                enter="transition duration-100 ease-out"
                enterFrom="transform h-0 opacity-0"
                enterTo="transform h-full opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform h-full opacity-100"
                leaveTo="transform h-0 opacity-0"
              >
                <Listbox.Options
                  className="dropdown-items"
                  ref={refs.setFloating}
                  style={{
                    ...floatingStyles,
                    zIndex: 999,
                  }}
                  static
                >
                  {options.map((option, i) => (
                    <Listbox.Option key={i} as={Fragment} value={option}>
                      {({ active, selected }) => (
                        <div
                          className={cx('dropdown-item justify-between', {
                            '!bg-pearl-grey': active,
                          })}
                        >
                          {option}
                          {selected && <Check width={16} height={16} />}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </FloatingFocusManager>
          </Portal>
        </>
      )}
    </Listbox>
  );
};

export default DropdownSelect;
